import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Eye, Download, ChevronDown, ChevronRight } from "lucide-react";

/**
 * iter67 · Phase C · Independent Line-Item Workflow (spec §9 · §12).
 *
 * Each line item is rendered as its own card with:
 *  - Per-item status pill + Update Status control (8 statuses)
 *  - Compact vendor block (confirmed vendor / PO / tracking / carrier / ETA)
 *  - Collapsible per-item timeline (status_history)
 *  - Preview + Download this item's own Vendor Packet PDF
 *  - Read-only spec summary (brand · color · size · qty · logo)
 *
 * Parent order status is auto-rolled up by the backend on every mutation.
 * Photos are managed in the separate `LineItemPhotosPanel` (iter66).
 */

const STATUS_STYLES = {
  Pending:              { fg: "#7A7A7A", bg: "#F4F3EC", border: "#E5E3D8" },
  Approved:             { fg: "#1A422D", bg: "#E9F0EB", border: "#1A422D" },
  Ordered:              { fg: "#8a6d2e", bg: "#FFFCEF", border: "#B59A5A" },
  Backordered:          { fg: "#8a1a1a", bg: "#fbe9e9", border: "#a33" },
  "Waiting on Vendor":  { fg: "#8a6d2e", bg: "#FFFCEF", border: "#B59A5A" },
  "Ready for Pickup":   { fg: "#0F5D3B", bg: "#E9F0EB", border: "#0F5D3B" },
  Completed:            { fg: "#3a5c48", bg: "#EAEEE9", border: "#2b432f" },
  Cancelled:            { fg: "#7A7A7A", bg: "#F4F3EC", border: "#B0B0B0" },
};

function StatusPill({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.Pending;
  return (
    <span
      className="inline-block px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] font-semibold border"
      style={{ color: s.fg, backgroundColor: s.bg, borderColor: s.border }}
      data-testid="line-item-status-pill"
    >
      {status || "Pending"}
    </span>
  );
}

function KV({ label, value }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-[0.22em] font-semibold text-[#B59A5A]">{label}</div>
      <div className="text-sm text-[#1A1A1A] mt-0.5">{value || <span className="text-[#a8a8a8]">—</span>}</div>
    </div>
  );
}

function ItemTimeline({ history, expanded }) {
  if (!expanded || !history?.length) return null;
  return (
    <ol className="mt-3 border-l-2 border-[#E5E3D8] pl-4 space-y-2" data-testid="line-item-timeline">
      {history.map((h, i) => (
        <li key={i} className="text-xs">
          <div className="text-[#1A422D] font-medium">{h.status}</div>
          <div className="text-[#7A7A7A]">
            {new Date(h.at).toLocaleString()} · {h.by_name || "system"}
            {h.action ? ` · ${h.action}` : ""}
          </div>
          {h.note && <div className="text-[#4A4A4A] italic mt-0.5">"{h.note}"</div>}
        </li>
      ))}
    </ol>
  );
}

function VendorBlock({ item, orderId, lineId, onUpdated }) {
  const [form, setForm] = useState({
    confirmed_vendor: item.confirmed_vendor || "",
    confirmed_vendor_id: item.confirmed_vendor_id || "",
    vendor_po: item.vendor_po || "",
    tracking_number: item.tracking_number || "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [vendorOptions, setVendorOptions] = useState([]);

  useEffect(() => {
    let cancelled = false;
    api.get("/admin/vendors/picker")
      .then(({ data }) => { if (!cancelled) setVendorOptions(data.vendors || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const anyValue = Object.values(form).some((v) => (v || "").toString().trim());

  const onVendorNameChange = (e) => {
    const name = e.target.value;
    const match = vendorOptions.find(
      (v) => (v.company_name || "").toLowerCase() === name.trim().toLowerCase(),
    );
    setForm((f) => ({
      ...f,
      confirmed_vendor: name,
      confirmed_vendor_id: match ? match.vendor_id : "",
    }));
  };

  const save = async () => {
    setSaving(true); setError(""); setSaved(false);
    try {
      const { data } = await api.patch(
        `/admin/orders/${orderId}/items/${lineId}/vendor`, form,
      );
      onUpdated?.(data);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err?.message || "Save failed.");
    } finally { setSaving(false); }
  };

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const listId = `line-vendor-picker-${lineId}`;

  return (
    <div className="mt-3 p-3 bg-[#F9F8F4] border border-[#E5E3D8]" data-testid="line-item-vendor-block">
      <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#B59A5A] mb-2">
        Vendor Assignment (this item)
        {form.confirmed_vendor_id && (
          <span className="ml-2 inline-block px-2 py-0.5 text-[9px] bg-[#E9F0EB] text-[#1A422D] border border-[#1A422D] normal-case tracking-widest" data-testid="line-vendor-picker-resolved">
            id linked
          </span>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        <input placeholder="Pick approved vendor or type" value={form.confirmed_vendor}
               onChange={onVendorNameChange} list={listId}
               data-testid="line-vendor-confirmed" className="input-field text-sm" />
        <datalist id={listId}>
          {vendorOptions.map((v) => (
            <option key={v.vendor_id} value={v.company_name}>
              {v.primary_category ? `${v.company_name} — ${v.primary_category}` : v.company_name}
            </option>
          ))}
        </datalist>
        <input placeholder="PO #" value={form.vendor_po} onChange={upd("vendor_po")}
               data-testid="line-vendor-po" className="input-field text-sm" />
        <input placeholder="Tracking #" value={form.tracking_number} onChange={upd("tracking_number")}
               data-testid="line-vendor-tracking" className="input-field text-sm" />
        <button type="button" onClick={save} disabled={saving || !anyValue}
                data-testid="line-vendor-save"
                className="btn-secondary text-xs">
          {saving ? "Saving…" : saved ? "Saved ✓" : "Save Vendor Info"}
        </button>
      </div>
      {error && <div className="mt-2 text-xs text-[#a33]">{error}</div>}
    </div>
  );
}

function LineItemCard({ item, idx, orderId, statuses, onUpdated }) {
  const lineId = item.line_id;
  const status = item.status_v2 || "Pending";
  const [selected, setSelected] = useState(status);
  const [note, setNote] = useState("");
  const [changing, setChanging] = useState(false);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => { setSelected(status); }, [status]);

  const apply = async () => {
    if (!selected || selected === status) return;
    setChanging(true); setError("");
    try {
      const { data } = await api.patch(
        `/admin/orders/${orderId}/items/${lineId}/status`,
        { status: selected, note: note.trim() || undefined },
      );
      onUpdated?.(data);
      setNote("");
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err?.message || "Update failed.");
    } finally { setChanging(false); }
  };

  const openPacketInTab = async (kind /* "preview" | "download" */) => {
    const setter = kind === "preview" ? setPreviewing : setDownloading;
    setter(true); setError("");
    try {
      const resp = await api.get(
        `/admin/orders/${orderId}/items/${lineId}/vendor-packet.pdf`,
        { responseType: "blob" },
      );
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      if (kind === "preview") {
        window.open(url, "_blank", "noopener,noreferrer");
        setTimeout(() => URL.revokeObjectURL(url), 20000);
      } else {
        const a = document.createElement("a");
        a.href = url; a.download = `vendor-packet-${orderId}-${item.item_name || lineId}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 4000);
      }
    } catch (err) {
      let d = err.message;
      if (err.response?.data instanceof Blob) {
        try { d = JSON.parse(await err.response.data.text()).detail || d; } catch (_e) { /* keep */ }
      }
      setError(`Item packet ${kind} failed: ${d}`);
    } finally { setter(false); }
  };

  const specs = [
    ["Brand", item.brand], ["Style #", item.style_number],
    ["Color", item.color], ["Size", item.size], ["Qty", item.quantity],
    ["Logo", item.logo_preference], ["Logo Location", item.logo_location],
    ["Logo Color", item.logo_color], ["Logo Thread Color", item.thread_color],
  ];

  return (
    <div className="border border-[#E5E3D8] bg-white p-4" data-testid={`line-item-card-${idx}`}>
      {/* Header — item name + status pill + expand */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#B59A5A]">
            Item {idx + 1} · <span className="text-[#7A7A7A] normal-case tracking-normal">{lineId}</span>
          </div>
          <div className="font-serif text-lg text-[#1A1A1A] mt-0.5">{item.item_name || "(unnamed)"}</div>
        </div>
        <div className="flex items-center gap-3">
          <StatusPill status={status} />
          <button type="button" onClick={() => setExpanded((v) => !v)}
                  data-testid={`line-item-toggle-${idx}`}
                  className="text-[#1A422D] hover:text-[#B59A5A]">
            {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </button>
        </div>
      </div>

      {/* Specs summary — always visible */}
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {specs.map(([k, v]) => <KV key={k} label={k} value={v ? String(v) : ""} />)}
      </div>

      {/* Update status row — always visible so staff can drive workflow fast */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <select value={selected} onChange={(e) => setSelected(e.target.value)}
                data-testid={`line-item-status-select-${idx}`}
                className="input-field text-sm max-w-[180px]">
          {(statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <input value={note} onChange={(e) => setNote(e.target.value)}
               placeholder="Note (optional)"
               data-testid={`line-item-status-note-${idx}`}
               className="input-field text-sm flex-1 min-w-[140px]" />
        <button type="button" onClick={apply}
                disabled={changing || selected === status}
                data-testid={`line-item-status-apply-${idx}`}
                className="btn-primary text-xs">
          {changing ? "Updating…" : "Update Status"}
        </button>
        <button type="button" onClick={() => openPacketInTab("preview")}
                disabled={previewing}
                data-testid={`line-item-packet-preview-${idx}`}
                className="inline-flex items-center gap-1 px-3 py-2 text-xs uppercase tracking-[0.18em] font-semibold border border-[#B59A5A] text-[#8a6d2e] hover:bg-[#FFFCEF]">
          <Eye size={13} /> {previewing ? "Opening…" : "Preview"}
        </button>
        <button type="button" onClick={() => openPacketInTab("download")}
                disabled={downloading}
                data-testid={`line-item-packet-download-${idx}`}
                className="inline-flex items-center gap-1 px-3 py-2 text-xs uppercase tracking-[0.18em] font-semibold border border-[#1A422D] text-[#1A422D] hover:bg-[#E9F0EB]">
          <Download size={13} /> {downloading ? "…" : "Item Packet"}
        </button>
      </div>

      {error && <div className="mt-2 text-xs text-[#a33]">{error}</div>}

      {/* Expanded — vendor block + timeline */}
      {expanded && (
        <>
          <VendorBlock item={item} orderId={orderId} lineId={lineId} onUpdated={onUpdated} />
          <ItemTimeline history={item.status_history} expanded={expanded} />
        </>
      )}
    </div>
  );
}

/** Rolled-up progress banner shown at the top of the panel. */
function ProgressBanner({ progress }) {
  if (!progress) return null;
  const { total = 0, closed = 0, open = 0, "Ready for Pickup": ready = 0 } = progress;
  const pct = total > 0 ? Math.round((closed / total) * 100) : 0;
  return (
    <div className="p-4 bg-[#F9F8F4] border border-[#E5E3D8] mb-4" data-testid="line-items-progress">
      <div className="flex items-center justify-between text-xs uppercase tracking-[0.22em] font-semibold">
        <span className="text-[#B59A5A]">Order Progress</span>
        <span className="text-[#1A422D]">{closed} / {total} closed{ready ? ` · ${ready} ready` : ""}</span>
      </div>
      <div className="mt-2 h-1.5 bg-[#E5E3D8]">
        <div className="h-1.5 bg-[#1A422D] transition-all" style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-1 text-[10px] text-[#7A7A7A]">
        {open > 0 ? `${open} item(s) still open — parent order stays open until every item is Completed or Cancelled.` : "All items closed."}
      </div>
    </div>
  );
}

export default function LineItemsPanel({ order, onUpdated }) {
  const items = order?.items || [];
  const [statuses, setStatuses] = useState(null);

  useEffect(() => {
    api.get("/admin/orders/options")
      .then(({ data }) => setStatuses(data.line_item_statuses || null))
      .catch(() => setStatuses(null));
  }, []);

  const progress = useMemo(() => order?.items_progress, [order]);

  // iter68 · Independent-line-item panel is only meaningful for multi-item
  // orders. Single-item orders drive their workflow via the legacy top-level
  // controls so we hide the whole panel to keep the page tidy.
  if (items.length <= 1) return null;

  return (
    <div className="mt-6 p-6 bg-white border border-[#E5E3D8]" data-testid="line-items-panel">
      <div className="mb-3">
        <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#B59A5A]">Independent Line Items</div>
        <h3 className="font-serif text-xl text-[#1A422D] mt-0.5">Each item — its own workflow</h3>
      </div>
      <ProgressBanner progress={progress} />
      <div className="space-y-3">
        {items.map((it, idx) => (
          <LineItemCard
            key={it.line_id || idx}
            item={it}
            idx={idx}
            orderId={order.order_id}
            statuses={statuses}
            onUpdated={onUpdated}
          />
        ))}
      </div>
    </div>
  );
}
